import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface Variant {
  _id: string;
  variant_name: string;
  [key: string]: any;
}

interface VariantCatalogState {
  loading: boolean;
  error: string | null;
  lastFetched: string | null;
  syncCompleted: boolean;
  syncProgress: number; // Percentage 0-100
  lastPageFetched: number;
  syncedCount: number;
  totalCount: number;
  resyncRequestId: number;
}

const initialState: VariantCatalogState = {
  loading: false,
  error: null,
  lastFetched: null,
  syncCompleted: false,
  syncProgress: 0,
  lastPageFetched: 0,
  syncedCount: 0,
  totalCount: 0,
  resyncRequestId: 0,
};

const variantCatalogSlice = createSlice({
  name: "variantCatalog",
  initialState,
  reducers: {
    setVariantsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    setVariantsFetched: (state) => {
      state.lastFetched = new Date().toISOString();
    },
    setVariantsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    setVariantSyncFinished: (state, action: PayloadAction<boolean>) => {
      state.syncCompleted = action.payload;
      if (action.payload) {
        state.loading = false;
        state.syncProgress = 100;
        state.lastFetched = new Date().toISOString();
      }
    },
    setVariantSyncProgress: (
      state,
      action: PayloadAction<{ progress: number; syncedCount?: number; totalCount?: number }>
    ) => {
      state.syncProgress = action.payload.progress;
      if (action.payload.syncedCount !== undefined) {
        state.syncedCount = action.payload.syncedCount;
      }
      if (action.payload.totalCount !== undefined) {
        state.totalCount = action.payload.totalCount;
      }
    },
    setLastPageFetched: (state, action: PayloadAction<number>) => {
      state.lastPageFetched = action.payload;
    },
    clearVariantCatalog: (state) => {
      state.lastFetched = null;
      state.syncCompleted = false;
      state.syncProgress = 0;
      state.lastPageFetched = 0;
      state.syncedCount = 0;
      state.totalCount = 0;
    },
    requestVariantResync: (state) => {
      state.resyncRequestId += 1;
      state.loading = false;
      state.error = null;
      state.lastFetched = null;
      state.syncCompleted = false;
      state.syncProgress = 0;
      state.lastPageFetched = 0;
      state.syncedCount = 0;
      state.totalCount = 0;
    },
  },
});

export const {
  setVariantsStart,
  setVariantsFetched,
  setVariantsFailure,
  setVariantSyncFinished,
  setVariantSyncProgress,
  setLastPageFetched,
  clearVariantCatalog,
  requestVariantResync,
} = variantCatalogSlice.actions;

// Selectors
export const selectVariantCatalogLoading = (state: {
  variantCatalog: VariantCatalogState;
}) => state.variantCatalog.loading;

export const selectIsVariantSyncFinished = (state: {
  variantCatalog: VariantCatalogState;
}) => state.variantCatalog.syncCompleted;

export const selectVariantLastFetched = (state: {
  variantCatalog: VariantCatalogState;
}) => state.variantCatalog.lastFetched;

export const selectVariantSyncProgress = (state: {
  variantCatalog: VariantCatalogState;
}) => state.variantCatalog.syncProgress;

export const selectLastPageFetched = (state: {
  variantCatalog: VariantCatalogState;
}) => state.variantCatalog.lastPageFetched;

export const selectVariantSyncCounts = (state: {
  variantCatalog: VariantCatalogState;
}) => ({
  syncedCount: state.variantCatalog.syncedCount,
  totalCount: state.variantCatalog.totalCount,
});

export const selectVariantResyncRequestId = (state: {
  variantCatalog: VariantCatalogState;
}) => state.variantCatalog.resyncRequestId;

export default variantCatalogSlice.reducer;
